import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, MoreHorizontal, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { UserAvatar, VerifiedTick } from "@/components/UserAvatar";
import { RichText } from "@/components/RichText";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, ago, mediaUrl, formatApiError } from "@/lib/api";
import { isAdmin, professionOf } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export function Comments({ base, onCount }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const { user } = useAuth();
  useEffect(() => {
    api.get(`${base}/comments`).then(({ data }) => setList(Array.isArray(data) ? data : data.comments || [])).catch(() => setList([]));
  }, [base]);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`${base}/comments`, { text });
      const c = data?.comment || data;
      setList((l) => [...(l || []), { ...c, author: c.author || user, text: c.text || text }]);
      onCount?.(1);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="mt-4 space-y-3 border-t pt-4" data-testid="comments-section">
      {(list || []).map((c, i) => {
        const a = c.author || c.user;
        return (
          <div key={c.id || i} className="flex gap-2.5" data-testid={`comment-${c.id || i}`}>
            <UserAvatar user={a} size="sm" />
            <div className="rounded-2xl bg-tm-muted px-3.5 py-2">
              <p className="text-xs font-semibold text-tm-green">{a?.full_name}</p>
              <p className="text-sm text-slate-700"><RichText text={c.text} /></p>
            </div>
          </div>
        );
      })}
      {user && (
        <form onSubmit={submit} className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment..." className="rounded-full" data-testid="comment-input" />
          <button type="submit" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-tm-green text-white" data-testid="comment-submit"><Send className="h-4 w-4" /></button>
        </form>
      )}
    </div>
  );
}

export const PostCard = ({ post: initial, onDelete }) => {
  const { user } = useAuth();
  const [post, setPost] = useState(initial);
  const [showComments, setShowComments] = useState(false);
  const canDelete = user && (user.id === post.author?.id || isAdmin(user));

  const like = async () => {
    if (!user) return toast.info("Log in to like posts");
    const liked = !post.liked;
    setPost((p) => ({ ...p, liked, likes_count: p.likes_count + (liked ? 1 : -1) }));
    try {
      await (liked ? api.post(`/posts/${post.id}/like`) : api.delete(`/posts/${post.id}/like`));
    } catch (e) {
      setPost((p) => ({ ...p, liked: !liked, likes_count: p.likes_count + (liked ? -1 : 1) }));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Post deleted");
      onDelete?.(post.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <article className="tm-card p-6 tm-fade-up" data-testid={`post-card-${post.id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/profile/${post.author?.id}`}><UserAvatar user={post.author} /></Link>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Link to={`/profile/${post.author?.id}`} className="font-semibold text-tm-green hover:underline">{post.author?.full_name}</Link>
            <VerifiedTick user={post.author} />
          </div>
          <p className="text-xs text-slate-500">{professionOf(post.author)} · {ago(post.created_at)}</p>
        </div>
        {post.category && <span className="hidden rounded-full bg-tm-muted px-2.5 py-1 text-xs font-medium text-tm-green sm:inline">{post.category}</span>}
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger className="rounded-full p-1.5 hover:bg-tm-muted" data-testid={`post-menu-${post.id}`}><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
            <DropdownMenuContent align="end"><DropdownMenuItem onClick={remove} className="text-red-600" data-testid={`post-delete-${post.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {post.title && <h3 className="mt-4 font-display text-lg font-bold text-tm-green">{post.title}</h3>}
      {post.text && <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-slate-800"><RichText text={post.text} /></p>}
      {post.images?.length > 0 && (
        <div className={`mt-4 grid gap-2 ${post.images.length > 1 ? "grid-cols-2" : ""}`}>
          {post.images.map((src) => <img key={src} src={mediaUrl(src)} alt="" className="max-h-[520px] w-full rounded-xl bg-tm-muted object-contain" />)}
        </div>
      )}
      {post.video && <video src={mediaUrl(post.video)} controls playsInline className="mt-4 max-h-[560px] w-full rounded-xl bg-black" data-testid={`post-video-${post.id}`} />}
      <div className="mt-4 flex items-center gap-6 text-sm text-slate-600">
        <button onClick={like} className={`flex items-center gap-1.5 transition-colors duration-200 ${post.liked ? "text-red-600" : "hover:text-red-600"}`} data-testid={`post-like-button-${post.id}`}>
          <Heart className={`h-[18px] w-[18px] ${post.liked ? "fill-current" : ""}`} /> <span data-testid={`post-like-count-${post.id}`}>{post.likes_count}</span>
        </button>
        <button onClick={() => setShowComments((s) => !s)} className="flex items-center gap-1.5 hover:text-tm-green" data-testid={`post-comment-toggle-${post.id}`}>
          <MessageCircle className="h-[18px] w-[18px]" /> {post.comments_count || 0}
        </button>
      </div>
      {showComments && <Comments base={`/posts/${post.id}`} onCount={(n) => setPost((p) => ({ ...p, comments_count: (p.comments_count || 0) + n }))} />}
    </article>
  );
};
